import { SquareIconButton } from '@components/SquareIconButton'
import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material'
import FeatherIcon from 'feather-icons-react'
import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { ChannelType, type CreateDefaultConfigFormData, type DefaultConfig, SupportType } from '../Templates/types'
import { DeleteDefaultConfigDialog } from './DeleteDefaultConfigDialog'

export interface DefaultConfigDetailsProps {
  config: DefaultConfig
  templates: { id: string; name: string }[]
  supportedCurrencies: string[]
  supportedPartners: string[]
  navigateBack: () => void
  onUpdate: (data: CreateDefaultConfigFormData) => void
  onDelete: () => void
  isUpdating: boolean
}

export const DefaultConfigDetails = ({
  config,
  templates,
  supportedCurrencies,
  supportedPartners,
  navigateBack,
  onUpdate,
  onDelete,
  isUpdating,
}: DefaultConfigDetailsProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const form = useForm<CreateDefaultConfigFormData>({
    defaultValues: {
      channel: config.channel,
      templateId: config.templateId,
      currencySupport: {
        type: config.currencySupport.type,
        currencies: config.currencySupport.currencies ?? [],
      },
    },
  })

  const channelType = useWatch({ control: form.control, name: 'channel.type' })
  const currencySupportType = useWatch({ control: form.control, name: 'currencySupport.type' })

  return (
    <Stack
      sx={{
        gap: 3,
      }}
    >
      <Stack
        sx={{
          gap: 3,
          alignItems: 'stretch',
        }}
      >
        <Stack
          sx={{
            alignItems: 'baseline',
          }}
        >
          <SquareIconButton onClick={navigateBack}>
            <FeatherIcon icon="chevron-left" />
          </SquareIconButton>
        </Stack>
        <Stack
          direction="row"
          sx={{
            flexGrow: 1,
            gap: 3,
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Typography variant="h1">
              {config.channel.type} — {config.channel.channelId}
            </Typography>
          </Stack>
          <Button color="error" variant="text" onClick={() => setShowDeleteDialog(true)}>
            Delete
          </Button>
        </Stack>
      </Stack>
      <form onSubmit={form.handleSubmit(onUpdate)}>
        <Stack
          sx={{
            gap: 3,
          }}
        >
          <Controller
            name="channel.type"
            control={form.control}
            render={({ field: { onChange, ...field } }) => (
              <FormControl fullWidth>
                <InputLabel id="channel-type-label">Channel type</InputLabel>
                <Select
                  {...field}
                  onChange={(e) => {
                    const value = e.target.value as ChannelType
                    form.reset({
                      channel: { type: value, channelId: '' },
                      templateId: config.templateId,
                      currencySupport: config.currencySupport,
                    })
                  }}
                  id="channel-type"
                  label="Channel type"
                  variant="outlined"
                  labelId="channel-type-label"
                >
                  <MenuItem value={ChannelType.AGENCY}>Agency</MenuItem>
                  <MenuItem value={ChannelType.PARTNER}>Partner</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          {channelType === ChannelType.AGENCY ? (
            <Controller
              name="channel.channelId"
              control={form.control}
              render={({ field }) => <TextField {...field} fullWidth id="channel-id" label="Agency ID" variant="outlined" />}
            />
          ) : (
            <Controller
              name="channel.channelId"
              control={form.control}
              render={({ field }) => (
                <Autocomplete
                  fullWidth
                  id="channel-id"
                  options={supportedPartners}
                  value={field.value || undefined}
                  disableClearable
                  onChange={(_event, selected) => {
                    field.onChange(selected ?? '')
                  }}
                  renderInput={(params) => <TextField {...params} label="Partner" variant="outlined" />}
                />
              )}
            />
          )}

          <Controller
            name="templateId"
            control={form.control}
            render={({ field }) => {
              const templateOptions = templates.map((t) => ({ label: t.name, value: t.id }))
              return (
                <Autocomplete
                  fullWidth
                  id="template-id"
                  options={templateOptions}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  value={templateOptions.find((o) => o.value === field.value) ?? undefined}
                  disableClearable
                  onChange={(_event, selected) => {
                    if (selected) {
                      field.onChange(selected.value)
                    }
                  }}
                  renderInput={(params) => <TextField {...params} label="Template" variant="outlined" />}
                />
              )
            }}
          />

          <Controller
            name="currencySupport.type"
            control={form.control}
            render={({ field: { onChange, ...field } }) => (
              <FormControl fullWidth>
                <InputLabel id="currency-support-type-label">Currency support</InputLabel>
                <Select
                  {...field}
                  onChange={(e) => onChange(e.target.value as SupportType)}
                  id="currency-support-type"
                  label="Currency support"
                  variant="outlined"
                  labelId="currency-support-type-label"
                >
                  <MenuItem value={SupportType.ALL}>All</MenuItem>
                  <MenuItem value={SupportType.SPECIFIC}>Specific</MenuItem>
                </Select>
              </FormControl>
            )}
          />

          {currencySupportType === SupportType.SPECIFIC && (
            <Controller
              name="currencySupport.currencies"
              control={form.control}
              render={({ field: { onChange, value, ...field } }) => (
                <FormControl fullWidth>
                  <InputLabel id="currencies-label">Currencies</InputLabel>
                  <Select
                    {...field}
                    onChange={(e) => onChange(e.target.value as string[])}
                    id="currencies"
                    label="Currencies"
                    variant="outlined"
                    labelId="currencies-label"
                    multiple
                    value={value ?? []}
                  >
                    {supportedCurrencies.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          )}

          <Button fullWidth type="submit" variant="contained" color="primary" disabled={isUpdating}>
            Save
          </Button>
        </Stack>
      </form>
      {showDeleteDialog && (
        <DeleteDefaultConfigDialog
          open
          config={config}
          onClose={() => setShowDeleteDialog(false)}
          onDelete={() => {
            setShowDeleteDialog(false)
            onDelete()
          }}
        />
      )}
    </Stack>
  )
}
